import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useHistory } from "react-router-dom"

import { BrokerCsvFileUploadField } from "@/components/Broker/BrokerCsvFileUploadField"
import CustomCsvImportWizard, {
  type CustomCsvWizardFooterAction,
} from "@/components/Broker/CustomCsvImport/CustomCsvImportWizard"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { FieldGroup } from "@/components/ui/field"
import type { BrokerCapabilities } from "@/services/brokerIntegrationApi"
import { fetchDemoCatalog, postDemoCohort } from "@/services/demoEntryAnalysisApi"
import type {
  DemoCatalogBroker,
  DemoCatalogFeGuidance,
  DemoParseContract,
} from "@/types/demoEntryAnalysis"
import type { CustomCsvFormFieldsInput } from "@/types/customCsvImport"
import { trackDemoEntryAnalysisEvent } from "@/utils/demoEntryAnalysisAnalytics"
import {
  demoCatalogToBrokerCapabilities,
  demoParseAcceptAttr,
  resolveDemoCatalogBroker,
  resolveDemoFileFormat,
  resolveDemoParse,
  resolveDemoUploadSubtitle,
} from "@/utils/demoEntryAnalysisBrokers"
import {
  DEMO_NONE_LOCATABLE_COPY,
  demoErrorUserMessage,
} from "@/utils/demoEntryAnalysisErrors"
import {
  DEMO_ENTRY_ANALYSIS_BASE_PATH,
  writeDemoSession,
} from "@/utils/demoEntryAnalysisSession"
import { applyDemoCentralInstrumentRows } from "@/utils/demoEntryAnalysisOhlc"
import { scanFuturesOrdersFileLatestFillUtcMs, DEMO_DATABENTO_RECENT_TAIL_MS } from "@/utils/demoFuturesRecentTailGate"
import {
  isCustomCsvBrokerName,
  resolveBrokerDisplayName,
} from "@/utils/customCsvAccountDisplay"
import { isFuturesOrdersCsvBroker } from "@/utils/futuresOrdersCsvBroker"
import { normalizeIanaTimeZone } from "@/utils/ianaTimezones"
import { resolveCustomCsvCapability } from "@/utils/customCsvImport"
import {
  defaultTradingViewLane,
  isTradingViewConnectBrandSlug,
  TRADINGVIEW_CONNECT_BRAND,
  type TradingViewLaneSlug,
} from "@/utils/tradingViewConnectBrand"
import { ConnectBrokerChooseStep } from "@/views/Dashboard/Profile/connectBroker/ConnectBrokerChooseStep"
import { ConnectBrokerSelectedBrokerHeading } from "@/views/Dashboard/Profile/connectBroker/ConnectBrokerSelectedBrokerHeading"
import { ConnectBrokerTradingViewLaneStep } from "@/views/Dashboard/Profile/connectBroker/ConnectBrokerTradingViewLaneStep"
import { normalizeConnectBrokerSlug } from "@/views/Dashboard/Profile/connectBroker/connectBrokerWizardConstants"
import { DemoBrokerFileUploadGuidance, DemoFileFormatHint } from "./DemoBrokerFileUploadGuidance"
import {
  AUTH_FONT,
  DemoEntryAnalysisShell,
  DemoLoadingBlock,
} from "./DemoEntryAnalysisShell"
import { DemoParseFields, demoParseRequiredMissing } from "./DemoParseFields"
import { DemoWizardFooter } from "./DemoWizardFooter"

type ImportPhase = "choose" | "tv_lane" | "upload"

/** Default selection on the demo broker step. */
const DEFAULT_DEMO_BROKER_SLUG = "liquidbrokers"

function isLiquidBrokersSlug(brokerName: string): boolean {
  const slug = normalizeConnectBrokerSlug(brokerName)
  return slug === "liquidbrokers" || slug === "liquid"
}

function resolveLiquidBrokersCatalogName(
  brokers: BrokerCapabilities[]
): string | null {
  const match = brokers.find((b) => isLiquidBrokersSlug(b.broker_name))
  return match?.broker_name ?? null
}

function defaultBrowserTimeZone(): string {
  try {
    return normalizeIanaTimeZone(
      Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
    )
  } catch {
    return "UTC"
  }
}

export default function DemoImportPage(): React.ReactElement {
  const history = useHistory()

  const [catalogBrokers, setCatalogBrokers] = useState<DemoCatalogBroker[]>([])
  const [feGuidance, setFeGuidance] = useState<DemoCatalogFeGuidance | null>(null)
  const [brokers, setBrokers] = useState<BrokerCapabilities[]>([])
  const [loadingCatalog, setLoadingCatalog] = useState(true)
  // Always land on step 1 (choose). Incomplete sessions previously jumped to upload.
  const [phase, setPhase] = useState<ImportPhase>("choose")
  const [gridSelection, setGridSelection] = useState(DEFAULT_DEMO_BROKER_SLUG)
  const [tradingViewLane, setTradingViewLane] = useState<TradingViewLaneSlug | null>(null)
  const [brokerName, setBrokerName] = useState("")
  const [file, setFile] = useState<File | null>(null)
  const [parseValues, setParseValues] = useState<Record<string, string>>(() => ({
    time_zone: defaultBrowserTimeZone(),
  }))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [customCsvFooterAction, setCustomCsvFooterAction] =
    useState<CustomCsvWizardFooterAction | null>(null)

  const demoParse: DemoParseContract | null = useMemo(
    () => resolveDemoParse(catalogBrokers, brokerName),
    [catalogBrokers, brokerName]
  )

  const catalogBroker = useMemo(
    () => resolveDemoCatalogBroker(catalogBrokers, brokerName),
    [catalogBrokers, brokerName]
  )

  const isCustom = isCustomCsvBrokerName(brokerName)

  const selectedCapability = useMemo(
    () => brokers.find((b) => b.broker_name === brokerName) || null,
    [brokers, brokerName]
  )

  const customCsvCapability = useMemo(() => {
    if (!selectedCapability || !isCustom) return null
    return resolveCustomCsvCapability(selectedCapability) ? selectedCapability : null
  }, [isCustom, selectedCapability])

  useEffect(() => {
    let cancelled = false
    setLoadingCatalog(true)
    fetchDemoCatalog()
      .then((res) => {
        if (cancelled) return
        const list = Array.isArray(res.brokers) ? res.brokers : []
        setCatalogBrokers(list)
        setFeGuidance(res.fe_guidance || null)
        const caps = demoCatalogToBrokerCapabilities(list)
        setBrokers(caps)
        const liquidName = resolveLiquidBrokersCatalogName(caps)
        if (liquidName) {
          setGridSelection((prev) =>
            !prev || isLiquidBrokersSlug(prev) ? liquidName : prev
          )
        }
        trackDemoEntryAnalysisEvent("import_catalog_loaded", {
          count: list.length,
          schema: res.schema_version || "",
        })
      })
      .catch((err) => {
        if (cancelled) return
        setError(demoErrorUserMessage(err))
        trackDemoEntryAnalysisEvent("import_catalog_failed")
      })
      .finally(() => {
        if (!cancelled) setLoadingCatalog(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!demoParse || isCustom) return
    const needsTz = (demoParse.required_fields || []).some(
      (f) => f === "time_zone" || f === "statement_time_zone"
    )
    if (needsTz && !parseValues.time_zone) {
      setParseValues((prev) => ({ ...prev, time_zone: defaultBrowserTimeZone() }))
    }
  }, [brokers, demoParse, feGuidance, parseValues.time_zone])

  useEffect(() => {
    if (!isCustom || phase !== "upload") {
      setCustomCsvFooterAction(null)
    }
  }, [isCustom, phase])

  const accept = demoParseAcceptAttr(demoParse)
  const fileFormat = resolveDemoFileFormat(demoParse)
  const displayName = selectedCapability
    ? resolveBrokerDisplayName(selectedCapability)
    : brokerName

  const finishCohort = useCallback(
    async (input: {
      file: File
      brokerName: string
      timeZone?: string
      importInstrument?: string
      utcOffsetHours?: string
      parseFields?: Record<string, string>
      layoutShapeId?: string
      importMode?: string
      columnMapping?: Record<string, string>
      valueTransforms?: Record<string, unknown>
      defaults?: Record<string, unknown>
      headerRowIndex?: number
    }) => {
      setBusy(true)
      setError(null)
      trackDemoEntryAnalysisEvent("import_started", { broker_name: input.brokerName })
      try {
        const cohort = await postDemoCohort({
          file: input.file,
          brokerName: input.brokerName,
          timeZone: input.timeZone,
          importInstrument: input.importInstrument,
          utcOffsetHours: input.utcOffsetHours,
          parseFields: input.parseFields,
          layoutShapeId: input.layoutShapeId,
          importMode: input.importMode,
          columnMapping: input.columnMapping,
          valueTransforms: input.valueTransforms,
          defaults: input.defaults,
          headerRowIndex: input.headerRowIndex,
        })

        const instruments = applyDemoCentralInstrumentRows(cohort.instruments || [])
        const anySupported = instruments.some(
          (row) => row.md_supported !== false && (row.trade_count || 0) > 0
        )
        if (!instruments.length || (!cohort.suggested_symbol && !anySupported)) {
          setError(DEMO_NONE_LOCATABLE_COPY)
          trackDemoEntryAnalysisEvent("import_failed", {
            broker_name: input.brokerName,
            stage: "cohort_empty",
          })
          return
        }

        const importTimeZone = input.timeZone
          ? normalizeIanaTimeZone(input.timeZone)
          : normalizeIanaTimeZone(
              Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
            )
        let latestFillUtcMs: number | null = null
        if (isFuturesOrdersCsvBroker(input.brokerName)) {
          const fillScan = await scanFuturesOrdersFileLatestFillUtcMs(
            input.file,
            importTimeZone
          )
          latestFillUtcMs = fillScan.latestFillUtcMs
          trackDemoEntryAnalysisEvent("futures_fill_scan", {
            broker_name: input.brokerName,
            fill_count: fillScan.fillCount,
            scanned: fillScan.scanned,
            has_recent_tail:
              latestFillUtcMs != null
                ? latestFillUtcMs > Date.now() - DEMO_DATABENTO_RECENT_TAIL_MS
                : false,
          })
        }

        writeDemoSession({
          step: "wizard",
          brokerName: input.brokerName,
          cohortId: cohort.cohort_id,
          instruments,
          suggestedSymbol: cohort.suggested_symbol,
          demoProcessedCount: cohort.demo_processed_count,
          demoSourceCount: cohort.demo_source_count,
          importTimeZone,
          latestFillUtcMs,
        })
        trackDemoEntryAnalysisEvent("import_ok", {
          broker_name: input.brokerName,
          instruments: instruments.length,
        })
        history.push(`${DEMO_ENTRY_ANALYSIS_BASE_PATH}/wizard`)
      } catch (err) {
        setError(demoErrorUserMessage(err))
        trackDemoEntryAnalysisEvent("import_failed", { broker_name: input.brokerName })
        throw err
      } finally {
        setBusy(false)
      }
    },
    [history]
  )

  const goUpload = (nextBroker: string) => {
    setBrokerName(nextBroker)
    writeDemoSession({ step: "import", brokerName: nextBroker })
    setFile(null)
    setParseValues({ time_zone: defaultBrowserTimeZone() })
    setPhase("upload")
  }

  const onChooseContinue = () => {
    if (!gridSelection) {
      setError("Select a broker to continue.")
      return
    }
    setError(null)
    trackDemoEntryAnalysisEvent("broker_selected", { broker_name: gridSelection })

    if (gridSelection === TRADINGVIEW_CONNECT_BRAND) {
      const lane = tradingViewLane ?? defaultTradingViewLane(brokers)
      setTradingViewLane(lane)
      setPhase("tv_lane")
      return
    }

    goUpload(gridSelection)
  }

  const onTvLaneContinue = () => {
    if (!tradingViewLane) {
      setError("Choose a TradingView path to continue.")
      return
    }
    setError(null)
    trackDemoEntryAnalysisEvent("tradingview_lane_selected", {
      broker_name: tradingViewLane,
    })
    goUpload(tradingViewLane)
  }

  const onContinueBrokerUpload = useCallback(async () => {
    if (!file || !brokerName || isCustom) {
      setError("Upload a trade history file to continue.")
      return
    }
    const missing = demoParseRequiredMissing(demoParse, feGuidance, parseValues, brokerName)
    if (missing) {
      setError(missing)
      return
    }

    const timeZone = parseValues.time_zone
      ? normalizeIanaTimeZone(parseValues.time_zone)
      : undefined
    const importInstrument = parseValues.import_instrument?.trim() || undefined
    const utcOffsetHours = parseValues.utc_offset_hours?.trim() || undefined

    try {
      await finishCohort({
        file,
        brokerName,
        timeZone,
        importInstrument,
        utcOffsetHours,
        parseFields: parseValues,
      })
    } catch {
      /* error already set */
    }
  }, [brokerName, demoParse, feGuidance, file, finishCohort, isCustom, parseValues])

  const onDemoCustomCsvReady = useCallback(
    async (payload: CustomCsvFormFieldsInput) => {
      if (!brokerName) return
      await finishCohort({
        file: payload.file,
        brokerName,
        timeZone: payload.timeZone ? normalizeIanaTimeZone(payload.timeZone) : undefined,
        importInstrument: payload.importInstrument?.trim() || undefined,
        layoutShapeId: payload.layoutShapeId,
        importMode: payload.importMode,
        columnMapping: payload.columnMapping,
        valueTransforms: payload.valueTransforms as Record<string, unknown>,
        defaults: payload.defaults as Record<string, unknown>,
        headerRowIndex: payload.headerRowIndex,
      })
    },
    [brokerName, finishCohort]
  )

  const activeStepIndex = phase === "upload" ? 1 : 0

  const shellTitle = phase === "choose" ? "Select your broker" : undefined

  const shellTitleMeta =
    phase === "upload" && brokerName ? (
      <ConnectBrokerSelectedBrokerHeading
        brokerName={
          isTradingViewConnectBrandSlug(brokerName)
            ? TRADINGVIEW_CONNECT_BRAND
            : brokerName
        }
        displayName={displayName || brokerName}
        title={isCustom ? "Custom CSV" : "Upload your trades"}
        description={
          isCustom
            ? "Same Custom CSV flow as Tessera: layout, map columns, formats, preview, then continue to analysis (nothing is saved)."
            : resolveDemoUploadSubtitle(brokerName)
        }
        titleAsPageHeading
        showBrokerTile={!isCustom}
      />
    ) : undefined

  const backToChooseOrTv = () => {
    setError(null)
    if (isTradingViewConnectBrandSlug(brokerName)) {
      setGridSelection(TRADINGVIEW_CONNECT_BRAND)
      setTradingViewLane(brokerName as TradingViewLaneSlug)
      setPhase("tv_lane")
      return
    }
    setPhase("choose")
  }

  const footer =
    phase === "choose" ? (
      <DemoWizardFooter
        onContinue={onChooseContinue}
        continueDisabled={!gridSelection || loadingCatalog}
      />
    ) : phase === "tv_lane" ? (
      <DemoWizardFooter
        onBack={() => {
          setPhase("choose")
          setError(null)
        }}
        showBack
        onContinue={onTvLaneContinue}
        continueDisabled={!tradingViewLane}
      />
    ) : isCustom ? (
      <DemoWizardFooter
        onBack={backToChooseOrTv}
        showBack
        showContinue={!customCsvFooterAction}
        continueDisabled
        continueLabel="Use the steps above"
        primaryFooterAction={customCsvFooterAction ?? undefined}
      />
    ) : (
      <DemoWizardFooter
        onBack={backToChooseOrTv}
        showBack
        onContinue={() => void onContinueBrokerUpload()}
        continueDisabled={!brokerName || !file || busy}
        continueLoading={busy}
        continueLoadingLabel="Reading file…"
      />
    )

  return (
    <DemoEntryAnalysisShell
      activeStepIndex={activeStepIndex}
      title={shellTitle}
      titleMeta={shellTitleMeta}
      footer={footer}
    >
      {loadingCatalog ? (
        <DemoLoadingBlock centered label="Loading brokers…" />
      ) : phase === "choose" ? (
        <div className="flex w-full min-w-0 flex-col gap-5">
          {error ? (
            <Alert variant="warning">
              <AlertDescription className={AUTH_FONT}>{error}</AlertDescription>
            </Alert>
          ) : null}

          <ConnectBrokerChooseStep
            brokers={brokers}
            connectedBrokerSlugs={new Set()}
            selectedBrokerName={gridSelection || null}
            onSelectBroker={(name) => {
              setGridSelection(name)
              setTradingViewLane(null)
              setBrokerName("")
              setError(null)
            }}
            hideSectionIntro
          />
        </div>
      ) : phase === "tv_lane" ? (
        <div className="flex w-full min-w-0 flex-col gap-5">
          {error ? (
            <Alert variant="warning">
              <AlertDescription className={AUTH_FONT}>{error}</AlertDescription>
            </Alert>
          ) : null}
          <ConnectBrokerTradingViewLaneStep
            brokers={brokers}
            selectedLane={tradingViewLane}
            onSelectLane={setTradingViewLane}
          />
        </div>
      ) : isCustom ? (
        <div className="flex w-full min-w-0 flex-col gap-4">
          {error ? (
            <Alert variant="warning">
              <AlertDescription className={AUTH_FONT}>{error}</AlertDescription>
            </Alert>
          ) : null}
          {!customCsvCapability ? (
            <Alert variant="error">
              <AlertDescription className={AUTH_FONT}>
                Custom CSV is not available in the demo catalog. Pick another broker or refresh.
              </AlertDescription>
            </Alert>
          ) : (
            <CustomCsvImportWizard
              mode="demo"
              capability={customCsvCapability}
              brokerAccounts={[]}
              onImportComplete={() => {
                /* demo never persists */
              }}
              onDemoReady={onDemoCustomCsvReady}
              onExitToParent={backToChooseOrTv}
              onEmbeddedFooterChange={setCustomCsvFooterAction}
            />
          )}
        </div>
      ) : (
        <FieldGroup className="gap-5">
          {error ? (
            <Alert variant="warning">
              <AlertDescription className={AUTH_FONT}>{error}</AlertDescription>
            </Alert>
          ) : null}

          <DemoBrokerFileUploadGuidance
            brokerName={brokerName}
            parse={demoParse}
            catalogBroker={catalogBroker}
          />

          <DemoFileFormatHint parse={demoParse} />

          <DemoParseFields
            parse={demoParse}
            guidance={feGuidance}
            brokerName={brokerName}
            values={parseValues}
            onChange={(formKey, value) =>
              setParseValues((prev) => ({ ...prev, [formKey]: value }))
            }
          />

          <BrokerCsvFileUploadField
            id="demo-entry-analysis-file"
            label={fileFormat.fieldLabel}
            description={fileFormat.fieldDescription}
            accept={accept}
            file={file}
            onFileChange={setFile}
            required
          />
        </FieldGroup>
      )}
    </DemoEntryAnalysisShell>
  )
}
